import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Drawer } from '@mui/material';
import { usePrivateQnaInfiniteListQuery, useDeletePrivateQnaMutation } from '../../../../queries';
import useInfiniteScroll from '../../../../hook/useInfiniteScroll';
import toast from 'react-hot-toast';
import PostCard from './PostCard';
import PostDetail from './PostDetail';
import OptionDialog from '../OptionDialog';


const LIMIT = 10;

function OneOnOneQuestion() {
  const pushedRef = useRef(false);
  const [contentOpenId, setContentOpenId] = useState(null);
  const [optionOpenId, setOptionOpenId] = useState(null);

  // TanStack Query 무한 스크롤 연동
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = usePrivateQnaInfiniteListQuery({
    limit: LIMIT,
  });

  const list = useMemo(
    () => data?.pages.flatMap((page: any) => page.items) || [],
    [data]
  );


  // 1:1 문의 삭제 Mutation
  const { mutate: deletePrivateQna } = useDeletePrivateQnaMutation();

  // infinite scroll
  const { lastItemRef } = useInfiniteScroll({
    hasMore: !!hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  });

  // 뒤로가기에 드로워가 닫히도록 설정
  const openDrawer = useCallback((id) => {
    setContentOpenId(id);
    window.history.pushState({ drawer: true }, '');
    pushedRef.current = true;
  }, []);

  const closeDrawer = useCallback(() => {
    setContentOpenId(null);
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (contentOpenId !== null) {
        setContentOpenId(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [contentOpenId]);

  const handleDeletePost = useCallback(() => {
    if (optionOpenId === null) return;
    deletePrivateQna(optionOpenId, {
      onSuccess: () => {
        setOptionOpenId(null);
      },
      onError: (error) => {
        console.error('Failed to delete privately submitted QnA', error);
        toast.error('1:1문의 글 삭제에 실패했습니다.');
      },
    });
  }, [optionOpenId, deletePrivateQna]);

  return (
    <div className="relative w-full">
      <div className="w-full pt-5">
        {list.length >= 1 &&
          list.map((content: any, idx: number) => {
            return (
              <PostCard
                key={content.id}
                post={content}
                onClick={(id) => openDrawer(id)}
                onOptionClick={(id) => setOptionOpenId(id)}
                ref={idx === list.length - 1 ? lastItemRef : undefined}
              />
            );
          })}
        {isLoading && (
          <div className="w-full py-5 text-center text-gray-500">
            로딩 중...
          </div>
        )}
      </div>
      <OptionDialog
        open={optionOpenId !== null}
        onClose={() => setOptionOpenId(null)}
        onDeleteClick={handleDeletePost}
      />
      <Drawer
        open={contentOpenId !== null}
        onClose={closeDrawer}
        anchor="right"
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
      >
        <PostDetail
          drawerOpen={contentOpenId !== null}
          onClickPrev={closeDrawer}
          postId={contentOpenId}
        />
      </Drawer>
    </div>
  );
}

export default OneOnOneQuestion;
